import {
    Injectable,
    inject
} from "@angular/core";
import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";
import {
    IFullAccessInternalApiClientService,
    InternalApiRequestService,
    ListUtil
} from "@shopliftr/common-ng";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    RequestUtil,
    BulkResponseItem,
    CustomOpResponse,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ModelAction,
    ISearchSort
} from "@shopliftr/common-js/data";
import { InvalidParametersException } from "../../../../exceptions/invalid-parameters-exception";
import {
    ProofsPage,
    ProofsPageOperation,
    ProofsPageSearchFilterOptions
} from "@shopliftr/common-js/smart";

/**
 * API Client Service for managing proofs pages.
 *
 * Implements IFullAccessInternalApiClientService to provide comprehensive CRUD operations
 * including bulk operations for ProofsPage entities via the Internal API v2.
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 */
@Injectable({
    providedIn: "root"
})
export class ProofsPageApiClientService implements IFullAccessInternalApiClientService<ProofsPage> {

    readonly basePath = "proofs-page";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Search for proofs pages with pagination and filtering.
     */
    search$(
        filterOptions: ProofsPageSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<ProofsPage>> {
        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ProofsPage.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$(searchRequest, this.basePath);
    }

    /**
     * Get all proofs pages matching the filter options.
     */
    getAll$(filterOptions: ProofsPageSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<ProofsPage>> {
        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            ProofsPage.modelName,
            0,
            100,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.getAll$(searchRequest, this.basePath);
    }

    /**
     * Get a proofs page by its id.
     */
    get$(proofsPageId: string): Observable<ProofsPage> {
        const getRequest = RequestUtil.createGetRequest<ProofsPage>(
            proofsPageId,
            ProofsPage.modelName,
            ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(
            map((response) => response.payload as ProofsPage)
        );
    }

    /**
     * Bulk get proofs pages by their ids.
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<ProofsPage>(id, ProofsPage.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Create a new proofs page.
     */
    create$(proofsPage: ProofsPage): Observable<ProofsPage> {
        const createRequest = RequestUtil.createCreateRequest<ProofsPage, void>(
            ModelAction.ClientCreate,
            proofsPage
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Bulk create proofs pages.
     */
    bulkCreate$(items: Array<CreateRequest<ProofsPage, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update an existing proofs page.
     */
    update$(proofsPageId: string, version: number, updateFields: ProofsPage, removeFields?: Array<string>): Observable<ProofsPage> {
        const fields = updateFields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<ProofsPage> = RequestUtil.createUpdateRequest(
            proofsPageId,
            ModelAction.ClientUpdate,
            fields,
            version,
            removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(
            map((response) => response.payload)
        );
    }

    /**
     * Update a proofs page with changes computed from original and updated models.
     */
    updateWithChanges$(original: ProofsPage, updated: ProofsPage): Observable<ProofsPage> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Bulk update proofs pages.
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a proofs page.
     */
    delete$(proofsPageId: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<ProofsPage>(
            proofsPageId,
            ProofsPage.modelName,
            ModelAction.ClientDelete,
            version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Bulk delete proofs pages.
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Generates a proofs page by id using a custom operation request.
     *
     * @param id - The id of the proofs page to generate
     * @returns Observable that completes when the generate operation finishes
     * @throws InvalidParametersException if id is not provided
     * @see InternalApiRequestService.customOp
     */
    generate$(id: string): Observable<void> {
        if (!id) {
            throw new InvalidParametersException("id is required");
        }

        const request = RequestUtil.createCustomOpRequest(
            id,
            ProofsPage.modelName,
            ProofsPageOperation.Generate
        );

        return this._internalApiRequestService.customOp$(request, this.basePath).pipe(
            map(() => {})
        );
    }

    /**
     * Bulk generates multiple proofs pages in a single bulk request.
     *
     * @param ids - Array of proofs page ids to generate
     * @returns Observable of bulk response items with operation results
     * @see InternalApiRequestService.bulk
     */
    bulkGenerate$(ids: ReadonlyArray<string>): Observable<Array<BulkResponseItem<CustomOpResponse<ProofsPage>>>> {
        const requests = ids.map((id) => {
            const customOpRequest = RequestUtil.createCustomOpRequest(
                id,
                ProofsPage.modelName,
                ProofsPageOperation.Generate
            );
            return BulkUtil.createRequestItem(customOpRequest);
        });
        const bulkRequest = BulkUtil.createBulkRequest(requests);

        return this._internalApiRequestService.bulk$(bulkRequest).pipe(
            map((response) => response.results as Array<BulkResponseItem<CustomOpResponse<ProofsPage>>>)
        );
    }
}
